import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import {
  meetingCaptureRailBtnSx,
  meetingCaptureRailSx,
  meetingDesktopAsideSx,
  meetingDesktopMainSx,
  meetingDesktopSplitSx,
  meetingSessionBodySx,
  meetingSessionHeaderSx,
  meetingStackedPanelSx,
} from "../Meetings/meetingUiStyles";
import {
  deleteFeriaMaterial,
  deleteFeriaPhoto,
  getFeriaEdicion,
  linkFeriaPhotosMaterialoteca,
  patchFeriaMediaGroups,
  summarizeFeriaEdicion,
  unlinkFeriaPhotosMaterialoteca,
  updateFeriaMaterial,
  uploadFeriaAudioChunk,
  uploadFeriaEdicionPhoto,
} from "../../api/Ferias/FeriasApi";
import {
  createFeriaMediaGroup,
  formatFeriaEdicionTitle,
  moveFeriaPhotosToGroup,
  parseFeriaMediaGroups,
  photoHasLinkedMaterial,
} from "../../utils/feriaSessionUtils";
import { formatElapsed } from "../../utils/meetingRecorder";
import { useMeetingRecorder } from "../Meetings/useMeetingRecorder";
import FeriaEdicionPanel from "./FeriaEdicionPanel";
import FeriaDeletePhotoDialog from "./FeriaDeletePhotoDialog";
import FeriaLinkMaterialotecaDialog from "./FeriaLinkMaterialotecaDialog";
import FeriaMediaGroupsBoard from "./FeriaMediaGroupsBoard";
import FeriaMediaPreviewDialog from "./FeriaMediaPreviewDialog";
import FeriaSupplierMaterialsWorkspace from "./FeriaSupplierMaterialsWorkspace";
import MaterialotecaPhotoLightbox from "../Materialoteca/MaterialotecaPhotoLightbox";
import { fetchFeriaPhotoAsFile } from "./feriaPhotoDrag";

const apiError = (err, fallback) => {
  const data = err?.response?.data;
  return (
    (typeof data === "string" && data) ||
    data?.error ||
    data?.message ||
    err?.message ||
    fallback
  );
};

/**
 * Workbench de sesión de edición de feria (media + resumen).
 */
const FeriaEdicionSessionPage = ({ edicionId, onBack, onChanged }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const [edicion, setEdicion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [linkOpen, setLinkOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryText, setSummaryText] = useState("");
  const [summaryBusy, setSummaryBusy] = useState(false);
  const [localPreviews, setLocalPreviews] = useState([]);
  const [audioCount, setAudioCount] = useState(0);
  /** Seed: foto de sesión soltada sobre un proveedor → alta + Vision */
  const [photoMaterialSeed, setPhotoMaterialSeed] = useState(null);
  /** Proveedor iluminado mientras se arrastra foto del tablero */
  const [supplierDragHoverId, setSupplierDragHoverId] = useState(null);
  const [focusSupplierId, setFocusSupplierId] = useState(null);
  const [requestEditMaterialId, setRequestEditMaterialId] = useState(null);
  const [assignMenu, setAssignMenu] = useState({
    anchor: null,
    feriaMaterialId: null,
  });
  const [materialDeleteTarget, setMaterialDeleteTarget] = useState(null);
  const [deletingMaterial, setDeletingMaterial] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [previewPhotoId, setPreviewPhotoId] = useState(null);
  /** Dock inferior «Materiales» (como Meetings loose dock). */
  const [inboxDockPx, setInboxDockPx] = useState(() => {
    try {
      const n = Number(localStorage.getItem("feriaInboxDockPx"));
      if (Number.isFinite(n) && n >= 36) return n;
    } catch {
      /* ignore */
    }
    return 200;
  });
  const inboxDockPxRef = useRef(inboxDockPx);
  inboxDockPxRef.current = inboxDockPx;

  /** Panel derecho proveedores: abierto o rail de iniciales. */
  const [suppliersAsideOpen, setSuppliersAsideOpen] = useState(() => {
    try {
      return localStorage.getItem("feriaSuppliersAsideOpen") !== "0";
    } catch {
      return true;
    }
  });
  const setSuppliersAsideOpenPersist = useCallback((open) => {
    setSuppliersAsideOpen(open);
    try {
      localStorage.setItem("feriaSuppliersAsideOpen", open ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, []);

  const onAudioChunk = useCallback(
    async ({ file, seq, durationMs }) => {
      await uploadFeriaAudioChunk(edicionId, file, { seq, durationMs });
      setAudioCount((n) => Math.max(n, Number(seq) + 1));
    },
    [edicionId],
  );

  const {
    isRecording,
    elapsedMs,
    pendingUploads,
    start: startRecording,
    stop: stopRecording,
    pauseForMediaCapture,
    resumeAfterMediaCapture,
  } = useMeetingRecorder({
    onChunk: onAudioChunk,
    onError: (err) => setError(apiError(err, "Error de grabación de audio")),
  });

  const load = useCallback(async () => {
    if (!edicionId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getFeriaEdicion(edicionId);
      setEdicion(data);
      setAudioCount(
        Number(data?.audioCount) ||
          (Array.isArray(data?.audioChunks) ? data.audioChunks.length : 0),
      );
      if (data?.summary?.summaryText) {
        setSummaryText(data.summary.summaryText);
      }
    } catch (err) {
      setError(apiError(err, t("FeriasPage.loadEditionError")));
      setEdicion(null);
    } finally {
      setLoading(false);
    }
  }, [edicionId, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setSelectedIds([]);
    setLocalPreviews([]);
  }, [edicionId]);

  const title = useMemo(() => formatFeriaEdicionTitle(edicion), [edicion]);

  const photos = useMemo(() => {
    const server = Array.isArray(edicion?.photos) ? edicion.photos : [];
    return [...localPreviews, ...server];
  }, [edicion?.photos, localPreviews]);

  /** Bandeja: fotos aún sin material vinculado. */
  const inboxPhotos = useMemo(
    () => (photos || []).filter((p) => !photoHasLinkedMaterial(p)),
    [photos],
  );

  /** Carrusel: navega por todas las fotos de la edición (como Meetings). */
  const previewNavIds = useMemo(
    () =>
      (photos || [])
        .map((p) => Number(p.id))
        .filter((id) => Number.isFinite(id) && id > 0),
    [photos],
  );

  const previewNavIndex = useMemo(() => {
    if (previewPhotoId == null) return -1;
    return previewNavIds.indexOf(Number(previewPhotoId));
  }, [previewNavIds, previewPhotoId]);

  const previewPhoto = useMemo(() => {
    if (previewPhotoId == null) return null;
    return (
      (photos || []).find((p) => Number(p.id) === Number(previewPhotoId)) ||
      null
    );
  }, [photos, previewPhotoId]);

  const openMedia = useCallback((photoId) => {
    const id = Number(photoId);
    if (!Number.isFinite(id) || id <= 0) return;
    setPreviewPhotoId(id);
  }, []);

  const closeMediaPreview = useCallback(() => {
    setPreviewPhotoId(null);
  }, []);

  const goPreviewPrev = useCallback(() => {
    if (previewNavIndex <= 0) return;
    setPreviewPhotoId(previewNavIds[previewNavIndex - 1]);
  }, [previewNavIds, previewNavIndex]);

  const goPreviewNext = useCallback(() => {
    if (previewNavIndex < 0 || previewNavIndex >= previewNavIds.length - 1)
      return;
    setPreviewPhotoId(previewNavIds[previewNavIndex + 1]);
  }, [previewNavIds, previewNavIndex]);

  const mediaGroups = useMemo(
    () => parseFeriaMediaGroups(edicion?.mediaGroups),
    [edicion?.mediaGroups],
  );

  const photoCount =
    Number(edicion?.photoCount) ||
    (Array.isArray(edicion?.photos) ? edicion.photos.length : 0);

  const applyEdicion = (next) => {
    if (!next) return;
    setEdicion(next);
    onChanged?.();
  };

  const persistGroups = async (nextGroups) => {
    setBusy(true);
    setError("");
    try {
      const data = await patchFeriaMediaGroups(edicionId, nextGroups);
      applyEdicion(data);
    } catch (err) {
      setError(apiError(err, "No se pudieron guardar los grupos"));
    } finally {
      setBusy(false);
    }
  };

  const handleToggleSelect = (id) => {
    const n = Number(id);
    if (!Number.isFinite(n)) return;
    setSelectedIds((prev) =>
      prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n],
    );
  };

  const startInboxDockResize = (event) => {
    if (event.button != null && event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const pointerId = event.pointerId;
    const startY = event.clientY;
    const startH = inboxDockPxRef.current;
    const onMove = (e) => {
      if (e.pointerId !== pointerId) return;
      const maxH = Math.max(
        220,
        Math.round((window.innerHeight || 800) * 0.55),
      );
      const next = Math.min(maxH, Math.max(44, startH - (e.clientY - startY)));
      setInboxDockPx(next);
    };
    const onUp = (e) => {
      if (e.pointerId !== pointerId) return;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      try {
        localStorage.setItem(
          "feriaInboxDockPx",
          String(inboxDockPxRef.current),
        );
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const handleDropPhotoOnSupplier = useCallback(
    async (photo, supplierId, photoIds = []) => {
      if (!photo || !supplierId) return;
      setSupplierDragHoverId(null);
      setFocusSupplierId(Number(supplierId));
      setBusy(true);
      setError("");
      try {
        const draggedId = Number(photo.id);
        const ids = (photoIds?.length ? photoIds : [draggedId])
          .map(Number)
          .filter((id) => Number.isFinite(id) && id > 0);
        const ordered = [
          draggedId,
          ...ids.filter((id) => id !== draggedId),
        ].filter((id) => Number.isFinite(id) && id > 0);

        // Primero la foto principal (abre el diálogo ya); el resto en paralelo
        const file = await fetchFeriaPhotoAsFile(photo);
        if (!file) {
          setError("No se pudo leer la foto para Vision");
          return;
        }
        setPhotoMaterialSeed({
          key: `${Date.now()}-${draggedId}-${ordered.length}`,
          supplierId: Number(supplierId),
          file,
          files: [file],
          photoIds: ordered,
        });
        setSelectedIds([]);
        if (ordered.length > 1) {
          Promise.all(
            ordered.slice(1).map(async (id) => {
              const p = (photos || []).find((ph) => Number(ph.id) === id);
              return p ? fetchFeriaPhotoAsFile(p) : null;
            }),
          ).then((rest) => {
            const extra = rest.filter(Boolean);
            if (!extra.length) return;
            setPhotoMaterialSeed((prev) =>
              prev && Number(prev.supplierId) === Number(supplierId)
                ? {
                    ...prev,
                    files: [prev.file || file, ...extra],
                  }
                : prev,
            );
          });
        }
      } catch (err) {
        setError(apiError(err, "No se pudo asignar la foto al proveedor"));
      } finally {
        setBusy(false);
      }
    },
    [photos],
  );

  const handleDropSessionPhotosOnSupplier = useCallback(
    async (supplierId, photoIds) => {
      const first = (photos || []).find(
        (p) => Number(p.id) === Number(photoIds?.[0]),
      );
      if (!first) return;
      await handleDropPhotoOnSupplier(first, supplierId, photoIds);
    },
    [photos, handleDropPhotoOnSupplier],
  );

  const handleNewGroup = async () => {
    const next = createFeriaMediaGroup(
      mediaGroups,
      "",
      selectedIds.length ? selectedIds : [],
    );
    setSelectedIds([]);
    await persistGroups(next);
  };

  const handleMoveSelected = async (targetGroupId) => {
    if (!selectedIds.length) return;
    const next = moveFeriaPhotosToGroup(
      mediaGroups,
      selectedIds,
      targetGroupId,
    );
    setSelectedIds([]);
    await persistGroups(next);
  };

  const handleDeleteSelected = async () => {
    if (!selectedIds.length) return;
    if (
      !window.confirm(
        `¿Eliminar ${selectedIds.length} foto(s)/vídeo(s)? Esta acción no se puede deshacer.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError("");
    try {
      for (const id of selectedIds) {
        await deleteFeriaPhoto(id);
      }
      setSelectedIds([]);
      await load();
      onChanged?.();
    } catch (err) {
      setError(apiError(err, "No se pudieron eliminar las fotos"));
    } finally {
      setBusy(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    const id = Number(photoId);
    if (!Number.isFinite(id) || id <= 0) return;
    setBusy(true);
    setError("");
    try {
      await deleteFeriaPhoto(id);
      setSelectedIds((prev) => prev.filter((x) => Number(x) !== id));
      await load();
      onChanged?.();
    } catch (err) {
      setError(apiError(err, "No se pudo eliminar la foto"));
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const confirmDeleteMaterial = async () => {
    const id = Number(materialDeleteTarget?.id_FeriaMaterial);
    if (!Number.isFinite(id) || id <= 0) return;
    setDeletingMaterial(true);
    setError("");
    try {
      await deleteFeriaMaterial(id);
      setMaterialDeleteTarget(null);
      await load();
      onChanged?.();
    } catch (err) {
      setError(apiError(err, "No se pudo eliminar el material"));
    } finally {
      setDeletingMaterial(false);
    }
  };

  const handleUnlink = async () => {
    if (!selectedIds.length) return;
    setBusy(true);
    setError("");
    try {
      const res = await unlinkFeriaPhotosMaterialoteca(edicionId, {
        photoIds: selectedIds,
      });
      if (res?.edicion) applyEdicion(res.edicion);
      else await load();
      setSelectedIds([]);
    } catch (err) {
      setError(apiError(err, "No se pudo desvincular"));
    } finally {
      setBusy(false);
    }
  };

  const handleLinkConfirm = async (body) => {
    setBusy(true);
    setError("");
    try {
      const res = await linkFeriaPhotosMaterialoteca(edicionId, {
        photoIds: selectedIds,
        ...body,
      });
      if (res?.edicion) applyEdicion(res.edicion);
      else await load();
      setSelectedIds([]);
      setLinkOpen(false);
    } catch (err) {
      setError(apiError(err, "No se pudo vincular a Materialoteca"));
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setBusy(true);
    setError("");
    const wasRecording = isRecording;
    try {
      if (wasRecording) await pauseForMediaCapture();
      for (const file of files) {
        const tempId = `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const preview = URL.createObjectURL(file);
        const tempPhoto = {
          id: null,
          _tempId: tempId,
          _localPreview: preview,
          mimeType: file.type,
          kind: String(file.type || "").startsWith("video/")
            ? "video"
            : "image",
          fileUrl: preview,
        };
        setLocalPreviews((prev) => [tempPhoto, ...prev]);
        try {
          await uploadFeriaEdicionPhoto(edicionId, file);
        } finally {
          URL.revokeObjectURL(preview);
          setLocalPreviews((prev) => prev.filter((p) => p._tempId !== tempId));
        }
      }
      await load();
      onChanged?.();
    } catch (err) {
      setError(apiError(err, "No se pudo subir el archivo"));
      setLocalPreviews([]);
    } finally {
      setBusy(false);
      if (wasRecording) {
        try {
          await resumeAfterMediaCapture();
        } catch (err) {
          setError(apiError(err, "No se pudo reanudar el audio"));
        }
      }
    }
  };

  const toggleRecording = async () => {
    setError("");
    try {
      if (isRecording) {
        await stopRecording();
        await load();
        return;
      }
      await startRecording(audioCount);
    } catch (err) {
      setError(apiError(err, "No se pudo iniciar el micrófono"));
    }
  };

  const handleSummarize = async () => {
    setSummaryBusy(true);
    setError("");
    try {
      const res = await summarizeFeriaEdicion(edicionId);
      setSummaryText(res?.summaryText || res?.errorMessage || "");
      setSummaryOpen(true);
      setEdicion((prev) =>
        prev ? { ...prev, summary: { ...(prev.summary || {}), ...res } } : prev,
      );
    } catch (err) {
      setError(apiError(err, "No se pudo generar el resumen"));
    } finally {
      setSummaryBusy(false);
    }
  };

  const reloadFromPanel = () => {
    load();
    onChanged?.();
  };

  if (loading && !edicion) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          minHeight: 240,
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        height: { md: "100%" },
        overflow: "hidden",
      }}
    >
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          uploadFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*,video/*"
        multiple
        hidden
        onChange={(e) => {
          uploadFiles(e.target.files);
          e.target.value = "";
        }}
      />

      <Box sx={meetingSessionHeaderSx(theme)}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ px: 1.5, py: 1.25, flexWrap: "wrap", rowGap: 1 }}
        >
          {onBack ? (
            <IconButton onClick={onBack} size="small" aria-label="Volver">
              <ArrowBackIcon />
            </IconButton>
          ) : null}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={800} noWrap>
              {title || t("FeriasPage.loadingEdition")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {photoCount} foto{photoCount === 1 ? "" : "s"}
              {edicion?.videoCount
                ? ` · ${edicion.videoCount} vídeo${
                    edicion.videoCount === 1 ? "" : "s"
                  }`
                : ""}
              {audioCount ? ` · ${audioCount} audio` : ""}
              {isRecording
                ? ` · REC ${formatElapsed(elapsedMs)}`
                : pendingUploads
                  ? ` · subiendo audio…`
                  : ""}
            </Typography>
          </Box>
          <Tooltip title="Resumir edición">
            <span>
              <Button
                size="small"
                startIcon={
                  summaryBusy ? (
                    <CircularProgress size={14} />
                  ) : (
                    <AutoAwesomeOutlinedIcon />
                  )
                }
                onClick={handleSummarize}
                disabled={summaryBusy || busy}
                sx={{ textTransform: "none" }}
              >
                Resumir
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Actualizar">
            <IconButton onClick={load} disabled={loading || busy} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      <Box
        sx={{
          ...meetingSessionBodySx,
          // Ferias: sin gris Meetings (#eef1f4); fondo real de la app
          bgcolor: "transparent",
          background: "transparent",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { md: 0.75 },
        }}
      >
        <Box
          sx={{
            ...meetingDesktopSplitSx({ railCollapsed: true }),
            // Menos hueco entre cámara/galería y la tarjeta central
            gap: { xs: 1, md: 0.75 },
            gridTemplateColumns: {
              xs: "1fr",
              md: "56px minmax(0, 1fr)",
              lg: "56px minmax(0, 1fr)",
              xl: "56px minmax(0, 1fr)",
            },
            flex: 1,
            minWidth: 0,
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              ...meetingDesktopAsideSx,
              ...meetingCaptureRailSx(theme),
              // Solo iconos: sin caja gris; mismo tope que el panel central
              bgcolor: "transparent !important",
              background: "transparent !important",
              border: "none",
              boxShadow: "none",
              p: 0,
              pt: 0,
              pb: 0,
              gap: 0.75,
              height: { md: "100%" },
              minHeight: { md: 0 },
              alignSelf: { md: "stretch" },
              justifyContent: { xs: "center", md: "flex-start" },
              alignItems: "center",
              overflow: "visible",
              position: { xs: "sticky", md: "relative" },
              top: { xs: 8, md: "auto" },
            }}
          >
            <Tooltip title="Cámara">
              <IconButton
                sx={{
                  ...meetingCaptureRailBtnSx,
                  bgcolor: "#fff",
                  border: "1px solid",
                  borderColor: alpha(theme.palette.text.primary, 0.12),
                  boxShadow: `0 1px 2px ${alpha("#0b1a24", 0.06)}`,
                  "&:hover": { bgcolor: "#fff" },
                }}
                onClick={() => cameraRef.current?.click()}
                disabled={busy}
              >
                <CameraAltOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Galería / archivos">
              <IconButton
                sx={{
                  ...meetingCaptureRailBtnSx,
                  bgcolor: "#fff",
                  border: "1px solid",
                  borderColor: alpha(theme.palette.text.primary, 0.12),
                  boxShadow: `0 1px 2px ${alpha("#0b1a24", 0.06)}`,
                  "&:hover": { bgcolor: "#fff" },
                }}
                onClick={() => galleryRef.current?.click()}
                disabled={busy}
              >
                <PhotoLibraryOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={isRecording ? "Parar audio" : "Grabar audio de feria"}
            >
              <IconButton
                sx={{
                  ...meetingCaptureRailBtnSx,
                  bgcolor: "#fff",
                  border: "1px solid",
                  borderColor: alpha(theme.palette.text.primary, 0.12),
                  boxShadow: `0 1px 2px ${alpha("#0b1a24", 0.06)}`,
                  color: isRecording ? "error.main" : undefined,
                  "&:hover": { bgcolor: "#fff" },
                }}
                onClick={toggleRecording}
                disabled={busy}
              >
                {isRecording ? (
                  <StopCircleOutlinedIcon />
                ) : (
                  <MicNoneOutlinedIcon />
                )}
              </IconButton>
            </Tooltip>
          </Box>

          <Box
            sx={{
              ...meetingDesktopMainSx,
              gap: 1.5,
              height: { md: "100%" },
              minHeight: { md: 0 },
              alignSelf: { md: "stretch" },
            }}
          >
            <Box
              sx={{
                ...meetingStackedPanelSx(theme),
                p: 1.5,
                flex: 1,
                minHeight: 200,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                gap: 0,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                  // Hueco para el marco de foco del proveedor (no se corta a la izquierda)
                  px: 0.75,
                  pt: 0.5,
                  pb: 1,
                }}
              >
                <FeriaSupplierMaterialsWorkspace
                  proveedores={edicion?.proveedores || []}
                  materiales={edicion?.materiales || []}
                  focusSupplierId={focusSupplierId}
                  dragHoverSupplierId={supplierDragHoverId}
                  showUnassigned={false}
                  onDropSessionPhotos={handleDropSessionPhotosOnSupplier}
                  onDesktopFilesDropOnSupplier={async (supplierRow, files) => {
                    const list = Array.from(files || []).filter(Boolean);
                    if (!list.length || !supplierRow?.id_FeriaProveedor) return;
                    setFocusSupplierId(Number(supplierRow.id_FeriaProveedor));
                    setPhotoMaterialSeed({
                      key: `${Date.now()}-desk-${supplierRow.id_FeriaProveedor}`,
                      supplierId: Number(supplierRow.id_FeriaProveedor),
                      file: list[0],
                      files: list,
                      photoIds: [],
                    });
                  }}
                  onEditMaterial={(row) =>
                    setRequestEditMaterialId(row.id_FeriaMaterial)
                  }
                  onDeleteMaterial={(row) => setMaterialDeleteTarget(row)}
                  onEstadoChange={async (row, Estado) => {
                    await updateFeriaMaterial(row.id_FeriaMaterial, { Estado });
                    await load();
                    onChanged?.();
                  }}
                  onAssignClick={(e, row) =>
                    setAssignMenu({
                      anchor: e.currentTarget,
                      feriaMaterialId: row.id_FeriaMaterial,
                    })
                  }
                  assignMenu={assignMenu}
                  onAssignMenuClose={() =>
                    setAssignMenu({ anchor: null, feriaMaterialId: null })
                  }
                  onAssignToSupplier={async (feriaMaterialId, supplierRow) => {
                    setAssignMenu({ anchor: null, feriaMaterialId: null });
                    const cod = supplierRow
                      ? supplierRow.CODPROV ||
                        supplierRow.proveedor?.CODPROV ||
                        null
                      : null;
                    const fab = supplierRow
                      ? supplierRow.id_Fabrica ||
                        supplierRow.fabrica?.id_Fabrica ||
                        null
                      : null;
                    const label = supplierRow
                      ? supplierRow.proveedor?.N_PROVEEDOR ||
                        supplierRow.fabrica?.NombreFabrica ||
                        ""
                      : "";
                    await updateFeriaMaterial(feriaMaterialId, {
                      CODPROV: cod ? Number(cod) : null,
                      Proveedor: cod ? Number(cod) : null,
                      id_Fabrica: fab ? Number(fab) : null,
                      MillName: fab && !cod ? label : null,
                    });
                    await load();
                    onChanged?.();
                  }}
                  onThumbClick={(src) => setLightboxSrc(src)}
                />
              </Box>

              {/* Dock inferior: fotos pendientes (raya arrastrable como Meetings) */}
              <Box
                sx={{
                  flexShrink: 0,
                  height: inboxDockPx,
                  minHeight: 44,
                  display: "flex",
                  flexDirection: "column",
                  borderTop: "1px solid",
                  borderColor: alpha(theme.palette.text.primary, 0.1),
                  bgcolor: alpha(theme.palette.text.primary, 0.02),
                  borderRadius: "12px 12px 0 0",
                  overflow: "hidden",
                }}
              >
                <Box
                  onPointerDown={startInboxDockResize}
                  sx={{
                    height: 14,
                    flexShrink: 0,
                    cursor: "ns-resize",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    touchAction: "none",
                    userSelect: "none",
                    "&:hover .feria-dock-grip": {
                      bgcolor: theme.palette.primary.main,
                    },
                  }}
                >
                  <Box
                    className="feria-dock-grip"
                    sx={{
                      width: 36,
                      height: 4,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.text.primary, 0.22),
                      transition: "background 0.12s ease",
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: "auto",
                    px: 0.5,
                    pb: 0.5,
                  }}
                >
                  {inboxDockPx > 52 ? (
                    <FeriaMediaGroupsBoard
                      photos={inboxPhotos}
                      mediaGroups={[]}
                      selectedIds={selectedIds}
                      onToggleSelect={handleToggleSelect}
                      onNewGroup={handleNewGroup}
                      onMoveSelected={handleMoveSelected}
                      onDeleteSelected={handleDeleteSelected}
                      onDeletePhoto={handleDeletePhoto}
                      onOpenMedia={openMedia}
                      onLinkMaterialoteca={() => setLinkOpen(true)}
                      onUnlinkMaterialoteca={handleUnlink}
                      onGroupsChange={persistGroups}
                      onDesktopFilesDrop={uploadFiles}
                      onDropPhotoOnSupplier={handleDropPhotoOnSupplier}
                      onSupplierDragHover={setSupplierDragHoverId}
                      inboxOnly
                      busy={busy}
                    />
                  ) : (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ px: 1 }}
                    >
                      Materiales · {inboxPhotos.length} pendiente
                      {inboxPhotos.length === 1 ? "" : "s"} — arrastra la raya
                      para abrir
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>

            {isMobile ? (
              <Accordion
                disableGutters
                elevation={0}
                sx={{
                  ...meetingStackedPanelSx(theme),
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={700}>Datos de la edición</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <FeriaEdicionPanel
                    edicionId={edicionId}
                    showHeader={false}
                    suppliersOnly
                    onChanged={reloadFromPanel}
                    photoMaterialSeed={photoMaterialSeed}
                    onPhotoMaterialSeedConsumed={() =>
                      setPhotoMaterialSeed(null)
                    }
                    onSessionMediaChanged={load}
                    dragHoverSupplierId={supplierDragHoverId}
                    onDropSessionPhotos={handleDropSessionPhotosOnSupplier}
                    requestEditMaterialId={requestEditMaterialId}
                    onRequestEditMaterialConsumed={() =>
                      setRequestEditMaterialId(null)
                    }
                    onFocusItem={(section, id) => {
                      if (section === "suppliers") setFocusSupplierId(id);
                    }}
                  />
                </AccordionDetails>
              </Accordion>
            ) : null}
          </Box>
        </Box>

        {!isMobile ? (
          <Box
            sx={{
              width: suppliersAsideOpen
                ? { md: 380, lg: 440 }
                : { md: 64, lg: 72 },
              flexShrink: 0,
              minHeight: 0,
              height: "100%",
              overflow: suppliersAsideOpen ? "auto" : "hidden",
              p: 0,
              bgcolor: "transparent",
              background: "transparent",
              border: "none",
              boxShadow: "none",
              transition: "width 0.18s ease",
            }}
          >
            <FeriaEdicionPanel
              edicionId={edicionId}
              showHeader={false}
              suppliersOnly
              compactRail={!suppliersAsideOpen}
              onToggleCompact={() =>
                setSuppliersAsideOpenPersist(!suppliersAsideOpen)
              }
              onChanged={reloadFromPanel}
              photoMaterialSeed={photoMaterialSeed}
              onPhotoMaterialSeedConsumed={() => setPhotoMaterialSeed(null)}
              onSessionMediaChanged={load}
              dragHoverSupplierId={supplierDragHoverId}
              onDropSessionPhotos={handleDropSessionPhotosOnSupplier}
              requestEditMaterialId={requestEditMaterialId}
              onRequestEditMaterialConsumed={() =>
                setRequestEditMaterialId(null)
              }
              onFocusItem={(section, id) => {
                if (section === "suppliers") setFocusSupplierId(id);
              }}
            />
          </Box>
        ) : null}
      </Box>

      <MaterialotecaPhotoLightbox
        open={Boolean(lightboxSrc)}
        src={lightboxSrc}
        onClose={() => setLightboxSrc(null)}
      />

      <FeriaMediaPreviewDialog
        open={Boolean(previewPhoto)}
        photo={previewPhoto}
        onClose={closeMediaPreview}
        onPrev={goPreviewPrev}
        onNext={goPreviewNext}
        canPrev={previewNavIndex > 0}
        canNext={
          previewNavIndex >= 0 && previewNavIndex < previewNavIds.length - 1
        }
        navIndex={previewNavIndex}
        navTotal={previewNavIds.length}
      />

      <FeriaDeletePhotoDialog
        open={Boolean(materialDeleteTarget)}
        variant="material"
        itemLabel={
          materialDeleteTarget?.material?.NombreMaterial ||
          materialDeleteTarget?.NombreMaterial ||
          ""
        }
        deleting={deletingMaterial}
        onClose={() => {
          if (!deletingMaterial) setMaterialDeleteTarget(null);
        }}
        onConfirm={confirmDeleteMaterial}
      />

      <FeriaLinkMaterialotecaDialog
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        onConfirm={handleLinkConfirm}
        materiales={edicion?.materiales || []}
        proveedores={edicion?.proveedores || []}
        busy={busy}
        selectedCount={selectedIds.length}
      />

      <Dialog
        open={summaryOpen}
        onClose={() => setSummaryOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Resumen de la edición</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", pt: 1 }}>
            {summaryText || "Sin texto de resumen todavía."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSummaryOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeriaEdicionSessionPage;
